import type { FC } from "react"
import React, { useState, useEffect } from "react"
import { BigNumber } from "@ethersproject/bignumber"
import { Skeleton, Stack, Flex, Text, Spacer, Icon } from "@chakra-ui/react"
import { Legend, LineChart } from "recharts"
import invariant from "tiny-invariant"

import type {
  Vote,
  ProposalTimelineChartQuery,
  ProposalTimelineChartQueryVariables,
} from "query/graphql"
import { ProposalTimelineChartDocument, VotesSortBy } from "query/graphql"
import { generateTimelineChartData } from "proposal/helpers/chart"
import EmptyEntity from "common/components/EmptyEntity"
import { bigNumberToNumber } from "common/helpers/bignumber"
import Key from "common/components/charts/timeline/Key"
import DataContainer from "common/components/charts/timeline/DataContainer"
import Grid from "common/components/charts/timeline/Grid"
import DateXAxis from "common/components/charts/timeline/DateXAxis"
import VotesYAxis from "common/components/charts/timeline/VotesYAxis"
import QuorumReferenceLine from "common/components/charts/timeline/QuorumReferenceLine"
import VotingTimelineLine from "common/components/charts/timeline/VotingTimelineLine"
import Tooltip from "common/components/charts/timeline/Tooltip"
import TallyBlack from "ui/components/icons/tally/TallyBlack"
import { useProposal } from "proposal/providers/ProposalProvider"
import { GrpcErrorType, fetcher } from "common/helpers/fetcher"

// Define a type for the proposal field
type ProposalData = Pick<ProposalTimelineChartQuery, "proposal">

type Props = {
  proposalId: string
  displayChart?: boolean
}

function isVote(node: any): node is Vote {
  return node && node.block && node.block.timestamp
}

const ProposalTimelineChart: FC<Props> = ({
  proposalId,
  displayChart = true,
}) => {
  const { isIncludePendingVotes } = useProposal()
  const [allVotes, setAllVotes] = useState<Vote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [proposalData, setProposalData] = useState<ProposalData | null>(null)

  const fetchVotes = async (
    afterCursor: string | null = null,
    pageCount = 0,
  ) => {
    if (pageCount >= 5) {
      setIsLoading(false)

      return
    }

    const data = await fetcher.gql<
      ProposalTimelineChartQuery,
      ProposalTimelineChartQueryVariables
    >({
      query: ProposalTimelineChartDocument,
      variables: {
        input: {
          id: proposalId,
        },
        votesInput: {
          filters: {
            proposalId,
            includePendingVotes: isIncludePendingVotes,
          },
          sort: {
            sortBy: VotesSortBy.Amount,
            isDescending: true,
          },
          page: {
            limit: 20,
            afterCursor,
          },
        },
      },
      omittedErrors: [GrpcErrorType.NotFound],
    })

    if (data) {
      setProposalData({ proposal: data.proposal })
      const newVotes = data.votes.nodes.filter(isVote)
      setAllVotes((prevVotes) => [...prevVotes, ...newVotes])

      // Check if the number of votes returned is less than the limit
      if (newVotes.length === 20 && data.votes.pageInfo?.lastCursor) {
        await fetchVotes(data.votes.pageInfo.lastCursor, pageCount + 1)
      } else {
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const loadVotes = async () => {
      setIsLoading(true)
      await fetchVotes()
    }

    if (displayChart) {
      loadVotes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId, displayChart, isIncludePendingVotes])

  if (!displayChart) {
    return null
  }

  if (isLoading) {
    return (
      <Flex as="section" direction="column">
        <Stack isInline>
          <Skeleton flex={1} h="54.915625rem" rounded="sm" />
        </Stack>
      </Flex>
    )
  }

  if (!proposalData || !proposalData.proposal?.createdAt) return null

  const { proposal } = proposalData
  const { createdAt, governor, quorum: proposalQuorum, organization } = proposal
  const ietfTimestamp = createdAt ?? ""

  invariant(governor, "governor is required")

  const { quorum: governorQuorum, token } = governor
  const { slug: organizationSlug } = organization

  const { decimals } = token

  const quorum = proposalQuorum ?? governorQuorum

  const hasVotes = Boolean(allVotes.length)

  if (!hasVotes) {
    return (
      <Flex direction="column" p={6} pb={0} w="full">
        <Stack align="center" direction="row" justify="center" width="100%">
          <Flex align="center" textAlign="left" width="100%">
            <Text as="h6" fontWeight="bold" textStyle="h6">
              Voting timeline
            </Text>
          </Flex>
          <Spacer />
          <Key />
        </Stack>
        <Flex align="center" direction="column" justify="center" mt="6">
          <EmptyEntity title="No votes yet" />
          <Flex align="right" justify="end" pr={1} width="100%">
            <Icon as={TallyBlack} color="gray.500" h={16} w={16} />
          </Flex>
        </Flex>
      </Flex>
    )
  }

  const bigQuorum = BigNumber.from(quorum)
  const quorumCount = bigNumberToNumber(bigQuorum, decimals)
  const dataset = generateTimelineChartData(allVotes, decimals, ietfTimestamp)
  const voters = allVotes.map(({ voter }) => voter)

  const isArbitrum = organizationSlug === "arbitrum"

  const showQuorumLine = !isArbitrum

  const renderLegend = () => {
    if (!Boolean(dataset.length >= 100)) {
      return null
    }

    return (
      <Flex justify="end" pt={6} w="full">
        <Text textStyle="body.regular.sm">Showing last 100 votes</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" p={6} pb={0} w="full">
      <Stack align="center" direction="row" justify="center" width="100%">
        <Flex align="center" textAlign="left" width="100%">
          <Text as="h6" fontWeight="bold" textStyle="h6">
            Voting timeline
          </Text>
        </Flex>
        <Spacer />
        <Key />
      </Stack>
      <DataContainer>
        <LineChart
          data={dataset}
          margin={{ top: 50, right: 0, bottom: 20, left: 0 }}
        >
          {Grid({})}
          {DateXAxis({})}
          {VotesYAxis({})}
          {showQuorumLine && QuorumReferenceLine({ y: quorumCount })}
          {Tooltip({ voters })}
          {VotingTimelineLine({ voters, decimals })}
          <Legend content={renderLegend} />
        </LineChart>
      </DataContainer>

      <Flex align="right" justify="end" pr={1} pt={4} width="100%">
        <Icon as={TallyBlack} color="gray.500" h={16} w={16} />
      </Flex>
    </Flex>
  )
}

export default ProposalTimelineChart
